import { useId, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { useOptionsSource } from '@/hooks/useOptionsSource'
import type { FetchPickerOptions, PickerOption } from '@/lib/projectOptions'
import { cn } from '@/lib/utils'

interface OptionsComboboxProps {
  id: string
  value: string
  source: string
  group: string | null
  awaitingParent: boolean
  fetchOptions: FetchPickerOptions
  onChangeText: (value: string) => void
  onPick: (option: PickerOption) => void
  describedBy?: string
  invalid?: boolean
  autoFocus?: boolean
}

/**
 * A free-text input that offers options from an options source while focused.
 * Typing and picking are co-equal: the list is a suggestion layer over an
 * ordinary text field, and no options (nothing configured, nothing matching,
 * fetch failed) simply means the field behaves as free text.
 */
export function OptionsCombobox({
  id,
  value,
  source,
  group,
  awaitingParent,
  fetchOptions,
  onChangeText,
  onPick,
  describedBy,
  invalid,
  autoFocus,
}: OptionsComboboxProps) {
  const listboxId = useId()
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const trimmed = value.trim()
  const options = useOptionsSource({
    fetchOptions,
    source,
    group,
    searchText: trimmed === '' ? null : trimmed,
    enabled: open && !awaitingParent,
  })

  const listOpen = open && options.length > 0
  const active = activeIndex >= 0 && activeIndex < options.length ? activeIndex : -1

  const pick = (option: PickerOption) => {
    onPick(option)
    setOpen(false)
    setActiveIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      if (options.length > 0) setActiveIndex(active < options.length - 1 ? active + 1 : 0)
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (options.length > 0) setActiveIndex(active > 0 ? active - 1 : options.length - 1)
      return
    }
    if (e.key === 'Enter') {
      if (!listOpen || active === -1) return
      const option = options[active]
      if (!option) return
      e.preventDefault()
      pick(option)
      return
    }
    if (e.key === 'Escape') {
      if (!listOpen) return
      e.preventDefault()
      setOpen(false)
      setActiveIndex(-1)
    }
  }

  return (
    <Popover open={listOpen}>
      <PopoverAnchor asChild>
        <Input
          id={id}
          role="combobox"
          spellCheck={false}
          autoComplete="off"
          autoFocus={autoFocus}
          placeholder={awaitingParent ? 'Type a value' : 'Type or pick a value'}
          value={value}
          aria-expanded={listOpen}
          aria-controls={listOpen ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={active === -1 ? undefined : `${listboxId}-${active}`}
          aria-invalid={invalid === true || undefined}
          aria-describedby={describedBy}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setOpen(false)
            setActiveIndex(-1)
          }}
          onChange={(e) => {
            onChangeText(e.target.value)
            setOpen(true)
            setActiveIndex(-1)
          }}
          onKeyDown={handleKeyDown}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="max-h-64 w-(--radix-popover-trigger-width) gap-0 overflow-y-auto p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Keep focus in the input so a click lands before blur closes the list.
        onMouseDown={(e) => e.preventDefault()}
      >
        <ul id={listboxId} role="listbox">
          {options.map((option, i) => (
            <li
              key={option.value}
              id={`${listboxId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={cn(
                'cursor-default rounded-md px-2 py-1.5 text-sm',
                i === active && 'bg-accent text-accent-foreground',
              )}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => pick(option)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
